// src/parametres/SocieteInfos.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Spinner, Form, InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function SocieteInfos() {
    const navigate = useNavigate();
    const [societe, setSociete] = useState({
        nom: '',
        adresse: '',
        telephone: '',
        email: '',
        site_web: '',
        rc: '',
        nif: '',
        patente: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchSociete();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchSociete = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setSociete(result.data);
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       HANDLERS
       ============================================================ */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setSociete(prev => ({ ...prev, [name]: value }));
    };

    const handleReset = async () => {
        await fetchSociete();
        showToast('info', 'Réinitialisé', 'Les modifications ont été annulées');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'update_societe',
                        ...societe
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Enregistré', 'Informations mises à jour avec succès');
            } else {
                showToast('error', 'Erreur', "Erreur lors de la sauvegarde");
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       LOADING — Bootstrap spinner (conservé)
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <Spinner
                                animation="border"
                                role="status"
                                className="mb-3"
                                style={{ width: '3rem', height: '3rem', color: COLORS.primary }}
                            >
                                <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                            <h5 className="text-muted">Chargement des informations...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-building-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Informations société</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-information-line"></i>
                                        Gérez les informations de votre entreprise
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate(-1)}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== FORMULAIRE ===== */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="card-header-gradient">
                            <h5 className="mb-0 text-white d-flex align-items-center">
                                <i className="ri-building-line me-2"></i>
                                Coordonnées de l'entreprise
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4">

                                    {/* SECTION 1 : Informations générales */}
                                    <Col md={12}>
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                <i className="ri-information-line"></i>
                                            </span>
                                            <h6>Informations générales</h6>
                                        </div>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">
                                                Nom de la société <span className="text-danger">*</span>
                                            </Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="nom"
                                                    value={societe.nom}
                                                    onChange={handleChange}
                                                    placeholder="Ma Société"
                                                    required
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">Site web</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-global-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="site_web"
                                                    value={societe.site_web}
                                                    onChange={handleChange}
                                                    placeholder="www.masociete.com"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">Adresse</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    name="adresse"
                                                    value={societe.adresse}
                                                    onChange={handleChange}
                                                    placeholder="Adresse complète"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    {/* SECTION 2 : Coordonnées */}
                                    <Col md={12}>
                                        <div className="section-title-charte mt-2">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                <i className="ri-phone-line"></i>
                                            </span>
                                            <h6>Coordonnées</h6>
                                        </div>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="telephone"
                                                    value={societe.telephone}
                                                    onChange={handleChange}
                                                    placeholder="+241 XX XX XX XX"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">Email</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={societe.email}
                                                    onChange={handleChange}
                                                    placeholder="contact@masociete.com"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    {/* SECTION 3 : Identifiants juridiques */}
                                    <Col md={12}>
                                        <div className="section-title-charte mt-2">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                                <i className="ri-file-copy-line"></i>
                                            </span>
                                            <h6>Identifiants juridiques</h6>
                                        </div>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">RC (Registre de Commerce)</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-barcode-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="rc"
                                                    value={societe.rc}
                                                    onChange={handleChange}
                                                    placeholder="RC123456"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">NIF (Identification Fiscale)</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="nif"
                                                    value={societe.nif}
                                                    onChange={handleChange}
                                                    placeholder="NIF12345678"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">Patente</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-passport-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="patente"
                                                    value={societe.patente}
                                                    onChange={handleChange}
                                                    placeholder="PAT123456"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    {/* BOUTONS D'ACTION */}
                                    <Col md={12}>
                                        <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                        <div className="d-flex justify-content-end gap-3 flex-wrap">
                                            <Button
                                                type="button"
                                                className="btn-modern-outline"
                                                onClick={handleReset}
                                            >
                                                <i className="ri-refresh-line me-1"></i> Réinitialiser
                                            </Button>
                                            <Button
                                                type="button"
                                                className="btn-modern-outline"
                                                onClick={() => navigate(-1)}
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="btn-modern-primary"
                                                disabled={saving}
                                            >
                                                {saving ? (
                                                    <><Spinner size="sm" className="me-2" />Sauvegarde...</>
                                                ) : (
                                                    <><i className="ri-save-line me-2"></i>Enregistrer</>
                                                )}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== FORMULAIRES ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
